// Generate a `cloudgrid.yaml` for the `plug` auto-init path (Option E).
//
// Unlike `cloudgrid init --type <T>`, auto-init does NOT scaffold starter files
// into the user's directory — they already have an app. The generated yaml
// declares one `web` service of the detected type, sourced from the project
// root (`source.path: .`, decision 124), so the existing files are what the
// tarball packs. The service gets `path: /` so it passes the backlog-082
// routability check and serves at the outlet root.

import { writeFileSync } from 'node:fs';
import { join } from 'node:path';

import type { DetectedBuild, DetectedType } from './framework-detect.js';

export interface GeneratedYamlSpec {
  /** Entity slug (the server-canonical name). */
  name: string;
  /** Detected service type. */
  type: DetectedType;
  /** Server-side build step for static-build frameworks (Vite / Astro /
   *  SvelteKit / Nuxt). Omitted for plain static sites, node, nextjs, python. */
  build?: DetectedBuild;
  /**
   * Top-level substrate needs to declare (BL621 gridify — Decision 042 Mode 1
   * §init behavior b). Each entry is a cloudgrid.yaml v2 need-word (database /
   * cache / vector / …) emitted as `<need>: true`. Omitted entirely when empty,
   * so the `plug` auto-init path — which never passes it — keeps its exact
   * output. Order is preserved as given.
   */
  needs?: string[];
  /**
   * Which command produced this yaml — drives the header comment only.
   * Defaults to `'plug'` so the existing auto-init output is byte-for-byte
   * unchanged; gridify passes `'init'`.
   */
  generatedBy?: 'plug' | 'init';
}

/** Build the yaml text. Exposed for unit assertions. */
export function buildGeneratedYaml(spec: GeneratedYamlSpec): string {
  const lines: string[] = [
    `# Generated by \`cloudgrid ${spec.generatedBy ?? 'plug'}\` auto-init.`,
    `# One service, sourced from this directory. Edit freely.`,
    `name: ${spec.name}`,
  ];
  // Top-level needs block (gridify). Sits between `name:` and `services:` so it
  // applies to the whole app, matching the v2 hybrid-scope default.
  if (spec.needs && spec.needs.length > 0) {
    lines.push(`needs:`);
    for (const need of spec.needs) {
      lines.push(`  ${need}: true`);
    }
  }
  lines.push(
    `services:`,
    `  web:`,
    `    type: ${spec.type}`,
    `    path: /`,
  );
  if (spec.build) {
    lines.push(
      `    build:`,
      `      command: ${spec.build.command}`,
      `      output: ${spec.build.output}`,
    );
  }
  lines.push(
    `    source:`,
    `      path: .`,
    ``,
  );
  return lines.join('\n');
}

/** Write `cloudgrid.yaml` into `dir`. Caller guarantees no yaml exists yet. */
export function writeGeneratedYaml(dir: string, spec: GeneratedYamlSpec): void {
  writeFileSync(join(dir, 'cloudgrid.yaml'), buildGeneratedYaml(spec), 'utf-8');
}
